// Shared OakGuard lockdown status (/api/lockdown/status) for the dashboard
// banner and the sidebar's oakGuard dot. Every team member sees it.
//
// One request serves every subscriber: the result is cached for a few
// seconds and refreshed on route change (like OrgAlertBanner). The oakGuard
// page calls notifyLockdownChanged() after a lock / mode switch / lift so the
// banner and dot update immediately instead of on the next navigation.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

export interface LockdownStatusLock {
  mode: 'recorded' | 'silent';
  reason: string | null;
  at: string;
  expires_at: string | null;
  by_email: string | null;
}

export interface LockdownStatus {
  active: boolean;
  team: LockdownStatusLock | null;
  orgs: Array<{ id: string; name: string | null; external_id: string; lock: LockdownStatusLock }>;
}

const CHANGED_EVENT = 'oak-lockdown-changed';
const FRESH_MS = 5_000;

let cached: { at: number; value: LockdownStatus } | null = null;
let inflight: Promise<LockdownStatus | null> | null = null;

function fetchStatus(force = false): Promise<LockdownStatus | null> {
  if (!force && cached && Date.now() - cached.at < FRESH_MS) return Promise.resolve(cached.value);
  if (inflight) return inflight;
  inflight = fetch('/api/lockdown/status', { credentials: 'include' })
    .then((r) => (r.ok ? (r.json() as Promise<LockdownStatus>) : null))
    .then((value) => {
      if (value) cached = { at: Date.now(), value };
      return value;
    })
    .catch(() => null)
    .finally(() => { inflight = null; });
  return inflight;
}

/** Call after changing a lockdown so the banner and sidebar dot refresh now. */
export function notifyLockdownChanged() {
  cached = null;
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

export function useLockdownStatus(): LockdownStatus | null {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<LockdownStatus | null>(cached?.value ?? null);

  useEffect(() => {
    let live = true;
    void fetchStatus().then((v) => { if (live && v) setStatus(v); });
    return () => { live = false; };
  }, [pathname]);

  useEffect(() => {
    const onChange = () => { void fetchStatus(true).then((v) => { if (v) setStatus(v); }); };
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, []);

  return status;
}
